import { Routes, Route } from "react-router-dom";
import { ROUTE_NAMES } from "./routeConstants";
import { ScrollToTop, ProtectedRoute } from "../components/shared/ui";

// Pages
import {
  LoginPage,
  RegistrationPage,
  VerifyEmailPage,
  EmployeeOnboardingPage,
  EmployerOnboardingPage,
  ForgotPasswordPage,
  ResetPasswordPage
} from "../pages/auth";
import { Jobs, PostJobPage, JobSeekerDashboardPage, EmployerDashboardPage, CompanyProfilePage } from "../pages/jobs";
import JobDetailsPage from "../pages/jobs/JobDetailsPage";
// Payment/Pricing pages - commented out (project is free to use at start)
// import { PricingPage, BillingDashboardPage } from "../pages/payments";
import { HomePage } from "../pages/home";
import { BlogPage } from "../pages/blog/BlogPage";
import { BlogArticlePage } from "../pages/blog/BlogArticlePage";
import { AboutPage, ContactPage, PrivacyPolicyPage, TermsOfServicePage, GDPRPage, UserProfilePage } from "../pages/shared";
import MessagesPage from "../pages/messages/MessagesPage";
import Layout from "../layout/Layout";

const AppRoutes = () => {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public routes with layout */}
        <Route
          path={ROUTE_NAMES.PAGES.HOME}
          element={
            <Layout>
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.LOGIN}
          element={
            <Layout>
              <ProtectedRoute redirectIfAuthenticated>
                <LoginPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.REGISTER}
          element={
            <Layout>
              <ProtectedRoute redirectIfAuthenticated>
                <RegistrationPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.VERIFY_EMAIL}
          element={
            <Layout>
              <VerifyEmailPage />
            </Layout>
          }
        />
        {/* Password Reset Routes */}
        <Route
          path={ROUTE_NAMES.PAGES.FORGOT_PASSWORD}
          element={
            <Layout>
              <ProtectedRoute redirectIfAuthenticated>
                <ForgotPasswordPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/forgot-password/reset"
          element={
            <Layout>
              <ProtectedRoute redirectIfAuthenticated>
                <ResetPasswordPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.ABOUT}
          element={
            <Layout>
              <AboutPage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.CONTACT}
          element={
            <Layout>
              <ContactPage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.PRIVACY}
          element={
            <Layout>
              <PrivacyPolicyPage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.TERMS}
          element={
            <Layout>
              <TermsOfServicePage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.GDPR}
          element={
            <Layout>
              <GDPRPage />
            </Layout>
          }
        />

        <Route
          path={ROUTE_NAMES.PAGES.BLOG}
          element={
            <Layout>
              <BlogPage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.BLOG_ARTICLE}
          element={
            <Layout>
              <BlogArticlePage />
            </Layout>
          }
        />

        {/* Layout wrapped routes */}
        <Route
          path={ROUTE_NAMES.PAGES.JOBS}
          element={
            <Layout>
              <Jobs />
            </Layout>
          }
        />
        <Route
          path="/jobs/:id"
          element={
            <Layout>
              <JobDetailsPage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.COMPANY_PROFILE}
          element={
            <Layout>
              <CompanyProfilePage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.USER_PROFILE}
          element={
            <Layout>
              <ProtectedRoute requireAuth>
                <UserProfilePage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.EMPLOYER_DASHBOARD}
          element={
            <Layout>
              <ProtectedRoute requireAuth allowedRoles={["employer"]}>
                <EmployerDashboardPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.JOB_SEEKER_DASHBOARD}
          element={
            <Layout>
              <ProtectedRoute requireAuth allowedRoles={["employee"]}>
                <JobSeekerDashboardPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.EMPLOYEE_ONBOARDING}
          element={
            <Layout>
              <ProtectedRoute requireAuth allowedRoles={["employee"]}>
                <EmployeeOnboardingPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.EMPLOYER_ONBOARDING}
          element={
            <Layout>
              <ProtectedRoute requireAuth allowedRoles={["employer"]}>
                <EmployerOnboardingPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.POST_JOB}
          element={
            <Layout>
              <ProtectedRoute requireAuth allowedRoles={["employer"]}>
                <PostJobPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.EDIT_JOB}
          element={
            <Layout>
              <ProtectedRoute requireAuth allowedRoles={["employer"]}>
                <PostJobPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        {/* Payment/Pricing routes - commented out (project is free to use at start)
        <Route
          path={ROUTE_NAMES.PAGES.PRICING}
          element={
            <Layout>
              <PricingPage />
            </Layout>
          }
        />
        <Route
          path={ROUTE_NAMES.PAGES.BILLING}
          element={
            <Layout>
              <BillingDashboardPage />
            </Layout>
          }
        />
        */}
        <Route
          path={ROUTE_NAMES.PAGES.MESSAGES}
          element={
            <Layout>
              <ProtectedRoute requireAuth>
                <MessagesPage />
              </ProtectedRoute>
            </Layout>
          }
        />
        <Route
          path="/messages/:conversationId"
          element={
            <Layout>
              <ProtectedRoute requireAuth>
                <MessagesPage />
              </ProtectedRoute>
            </Layout>
          }
        />

        {/* Catch-all */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold mb-4">404</h1>
                <p className="text-gray-600 mb-4">Page not found</p>
                <a href="/" className="text-blue-600 hover:underline">Go home</a>
              </div>
            </div>
          }
        />
      </Routes>
    </>
  );
};

export default AppRoutes;
